import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopics } from '../services/topics';

const TopicsWidget = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  const fetchTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTopics({ limit: 8, unread_only: true });
      setTopics(response.topics || []);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleTopicClick = (topicId) => {
    // Navigate to topic detail view
    navigate(`/topics/${topicId}`);
  };

  // Load topics on component mount
  useEffect(() => {
    fetchTopics();
  }, []);

  const formatScore = (score) => {
    return Math.round(score * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-blue-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-secondary-text';
  };

  const formatRecency = (days) => {
    if (days < 1) return 'today';
    if (days < 2) return '1 day ago';
    if (days < 7) return `${Math.round(days)} days ago`;
    if (days < 30) return `${Math.round(days / 7)} weeks ago`;
    return `${Math.round(days / 30)} months ago`;
  };

  return (
    <div className="bg-surface rounded-lg shadow-md p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold text-heading">
            📊 Topics
          </h2>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-3 text-secondary-text hover:text-body-text text-sm"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▶' : '▼'}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
              <span className="ml-3 text-secondary-text">Loading topics...</span>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8 text-secondary-text">
              <div className="text-4xl mb-2">📚</div>
              <p className="text-lg font-medium">No topics found</p>
              <p className="text-sm mt-1">
                Topics are automatically generated when you have unread articles
              </p>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {topics.map((topic) => (
                  <div
                    key={topic.id}
                    onClick={() => handleTopicClick(topic.id)}
                    className="border border-theme-border-subtle rounded-lg p-4 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-heading truncate pr-2">
                        {topic.label}
                      </h3>
                      <div className={`text-sm font-semibold ${getScoreColor(topic.final_score)}`}>
                        {formatScore(topic.final_score)}%
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-y-1 text-xs sm:text-sm text-secondary-text">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                        <span title="Unread articles">
                          📄 {topic.unread_count || 0} unread
                        </span>
                        <span title="Total articles in cluster">
                          📚 {topic.cluster_size || topic.article_count || 0} total
                        </span>
                      </div>
                      <span title="Average article age" className="text-xs">
                        {formatRecency(topic.avg_recency_days)}
                      </span>
                    </div>

                    {topic.avg_feedback_score !== 0 && (
                      <div className="mt-2 text-xs text-secondary-text">
                        Avg feedback: {topic.avg_feedback_score > 0 ? '👍' : '👎'}
                        {Math.abs(topic.avg_feedback_score).toFixed(1)}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {topics.length > 0 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate('/topics')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                  >
                    View all topics →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopicsWidget;